'use client'

import type { ReactNode } from 'react'

import {
  Check,
  ChevronDown,
  Pilcrow,
  PilcrowLeft,
  PilcrowRight
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'

import { useMediaQuery } from '@/hooks/use-media-query'

import { MobileToolbarGroup, MobileToolbarItem } from './mobile-toolbar-group'
import { useToolbar } from './toolbar-provider'

type DirectionValue = 'auto' | 'ltr' | 'rtl' | 'unset'

export const DirectionToolbar = () => {
  const { editor, hasCommand, hasExtension } = useToolbar()
  const t = useTranslations('components.tiptap.toolbars.direction')
  const isMobile = useMediaQuery('(max-width: 640px)')
  const directionOptions: Array<{
    icon: ReactNode
    label: string
    value: DirectionValue
  }> = [
    { icon: <Pilcrow className="h-4 w-4" />, label: t('none'), value: 'unset' },
    {
      icon: <PilcrowRight className="h-4 w-4" />,
      label: t('ltr'),
      value: 'ltr'
    },
    {
      icon: <PilcrowLeft className="h-4 w-4" />,
      label: t('rtl'),
      value: 'rtl'
    },
    { icon: <Pilcrow className="h-4 w-4" />, label: t('auto'), value: 'auto' }
  ]

  const hasDirectionSupport =
    hasExtension('textDirection') &&
    hasCommand('setTextDirection') &&
    hasCommand('unsetTextDirection')

  if (!hasDirectionSupport) {
    return null
  }

  const paragraphDir = editor?.getAttributes('paragraph')?.dir as
    | 'auto'
    | 'ltr'
    | 'rtl'
    | undefined
  const headingDir = editor?.getAttributes('heading')?.dir as
    | 'auto'
    | 'ltr'
    | 'rtl'
    | undefined
  const currentDirection: DirectionValue = (paragraphDir ||
    headingDir ||
    'unset') as DirectionValue

  const setDirection = (value: DirectionValue) => {
    if (value === 'unset') {
      editor?.chain().focus().unsetTextDirection().run()
      return
    }

    editor?.chain().focus().setTextDirection(value).run()
  }

  const activeLabel =
    directionOptions.find((option) => option.value === currentDirection)
      ?.label ?? t('none')
  const activeIcon = directionOptions.find(
    (option) => option.value === currentDirection
  )?.icon ?? <Pilcrow className="h-4 w-4" />

  if (isMobile) {
    return (
      <MobileToolbarGroup label={t('mobileLabel', { direction: activeLabel })}>
        {directionOptions.map((option) => (
          <MobileToolbarItem
            active={currentDirection === option.value}
            key={option.value}
            onClick={() => setDirection(option.value)}
          >
            {option.label}
          </MobileToolbarItem>
        ))}
      </MobileToolbarGroup>
    )
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-8 w-max font-normal"
              size="sm"
              type="button"
              variant="ghost"
            >
              {activeIcon}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('title')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        loop
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        <DropdownMenuGroup className="w-28">
          {directionOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => {
                setDirection(option.value)
              }}
            >
              <span className="mr-2">{option.icon}</span>
              {option.label}
              {option.value === currentDirection && (
                <Check className="ml-auto h-4 w-4" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
